import { env } from "../env";
import { useAuthStore } from "../store/authStore";

const BASE_URL = `${env.VITE_API_URL}/api`;

// The admin password unlocks one browser tab at a time. sessionStorage is per tab and is
// cleared when the tab closes, so a new tab has no token and the server asks again.
const ADMIN_UNLOCK_KEY = "tc_admin_unlock";

export function setAdminUnlock(token: string): void {
  try {
    sessionStorage.setItem(ADMIN_UNLOCK_KEY, token);
  } catch {
    // Storage blocked: this tab will simply be asked for the password again
  }
}

export function clearAdminUnlock(): void {
  try {
    sessionStorage.removeItem(ADMIN_UNLOCK_KEY);
  } catch {
    // ignore
  }
}

function getAdminUnlock(): string | null {
  try {
    return sessionStorage.getItem(ADMIN_UNLOCK_KEY);
  } catch {
    return null;
  }
}

interface FetchOptions extends RequestInit {
  json?: unknown;
}

export type MoveConflictPayload = {
  id: string;
  kind: string;
  reason: string;
};

export class ApiError extends Error {
  constructor(
    public readonly status: number,
    message: string,
    public readonly code?: string,
    public readonly conflicts?: MoveConflictPayload[]
  ) {
    super(message);
    this.name = "ApiError";
  }
}

async function request<T>(path: string, options: FetchOptions = {}): Promise<T> {
  const { json, headers = {}, ...rest } = options;

  const adminUnlock = getAdminUnlock();
  const resolvedHeaders: Record<string, string> = {
    ...(json ? { "Content-Type": "application/json" } : {}),
    ...(adminUnlock ? { "X-Admin-Unlock": adminUnlock } : {}),
    ...(headers as Record<string, string>),
  };

  const response = await fetch(`${BASE_URL}${path}`, {
    credentials: "include",
    headers: resolvedHeaders,
    ...(json !== undefined ? { body: JSON.stringify(json) } : {}),
    ...rest,
  });

  if (response.status === 401) {
    // Throw so the caller (route guard / react-query) can handle unauthenticated state.
    // Do NOT redirect here — that causes infinite reload loops on init.
    throw new ApiError(401, "Authentication required");
  }

  const data = (await response.json()) as {
    success: boolean;
    error?: string;
    code?: string;
    data?: T;
    conflicts?: MoveConflictPayload[];
  };

  if (!response.ok || !data.success) {
    if (data.code === "ADMIN_PASSWORD_REQUIRED") {
      // This tab's unlock expired (24 h) or was never there: show the password screen again
      clearAdminUnlock();
      const { user, setUser } = useAuthStore.getState();
      if (user && user.adminPasswordStatus === "VERIFIED") {
        setUser({ ...user, adminPasswordStatus: "VERIFY_REQUIRED" });
      }
    }
    throw new ApiError(
      response.status,
      data.error ?? "Request failed",
      data.code,
      data.conflicts
    );
  }

  return data.data as T;
}

export const api = {
  get: <T>(path: string, options?: FetchOptions) =>
    request<T>(path, { method: "GET", ...options }),

  post: <T>(path: string, json?: unknown, options?: FetchOptions) =>
    request<T>(path, { method: "POST", json, ...options }),

  patch: <T>(path: string, json?: unknown, options?: FetchOptions) =>
    request<T>(path, { method: "PATCH", json, ...options }),

  delete: <T>(path: string, options?: FetchOptions) =>
    request<T>(path, { method: "DELETE", ...options }),
};
